// Reusable loading spinner. Plan 24.
//
// Wraps the curated `Loader2` icon with a sized `animate-spin` container
// and an accessible `role="status"` / hidden label so screen readers
// announce loading states. Callers that already provide live-region
// framing (e.g. `<PageFallback />`, buttons with inline spinners) can
// pass `srOnly={false}` via their own `aria-label` and skip the nested
// `<span class="sr-only">`.

import type { AriaAttributes } from 'react';
import { Loader2 } from '../icons';
import { cn } from '../utils/cn';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps extends AriaAttributes {
  size?: SpinnerSize;
  className?: string;
  /** Screen-reader label; default "Loading". Pass "" for purely decorative. */
  label?: string;
}

const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-10 h-10',
};

export const Spinner = ({ size = 'md', className, label = 'Loading', ...aria }: SpinnerProps) => {
  const decorative = label === '';
  return (
    <span
      role={decorative ? undefined : 'status'}
      aria-live={decorative ? undefined : 'polite'}
      className={cn('inline-flex items-center justify-center', className)}
      {...aria}
    >
      <Loader2 className={cn(SIZE_CLASSES[size], 'animate-spin')} aria-hidden="true" />
      {!decorative && <span className="sr-only">{label}</span>}
    </span>
  );
};
